import {
  CALL_TYPES,
  DATA_KEYS,
  DATA_KEY_PRESETS,
  INTERFACE_PRESETS,
  PERMISSIONS,
  PERMISSION_RISK,
  PERMISSION_PRESETS,
} from './constants'
import { 
  concatHex,
  stringToHex,
  toHex, 
  pad,
  toFunctionSelector,
  keccak256,
  hexToString,
  getAddress,
  type Hex,
  type Address,
  type PublicClient,
} from 'viem'

/**
 * Combine multiple permissions into a single bytes32 value
 */
export function combinePermissions(permissions: (string | bigint)[]): Hex {
  let combined = 0n
  for (const perm of permissions) {
    combined |= BigInt(perm)
  }
  return pad(toHex(combined), { size: 32 })
}

/**
 * Decode a permissions bytes32 into individual permission names
 */
export function decodePermissions(permissionValue: string): string[] {
  const permissions: string[] = []
  const value = BigInt(permissionValue)
  
  for (const [name, hex] of Object.entries(PERMISSIONS)) {
    if (name === 'ALL_PERMISSIONS') continue
    if ((value & BigInt(hex)) !== 0n) {
      permissions.push(name)
    }
  }
  
  return permissions
}

/**
 * Derive a URL-style slug from a display name (e.g. "Forever Moments — Gasless" → "forever-moments/gasless").
 */
export function slugifyFromName(name: string): string {
  const t = name.trim()
  if (!t) return ''
  const withSlash = t.replace(/\s*[—–-]\s*/g, '/')
  return withSlash
    .toLowerCase()
    .replace(/[^a-z0-9/\s]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/\/-+/g, '/')
    .replace(/-+\//g, '/')
    .replace(/^[-/]+|[-/]+$/g, '')
}

/**
 * Check if a permission value includes a specific permission
 */
export function hasPermission(permissionValue: string | bigint, permission: string | bigint): boolean {
  return (BigInt(permissionValue) & BigInt(permission)) !== 0n
}

/**
 * Get the data key for a controller's permissions
 */
export function getPermissionsDataKey(controllerAddress: Address): Hex {
  const prefix = DATA_KEYS['AddressPermissions:Permissions_prefix']
  const addressBytes = controllerAddress.slice(2).toLowerCase()
  return `${prefix}${addressBytes}` as Hex
}

/**
 * Get the data key for a controller's AllowedCalls
 */
export function getAllowedCallsDataKey(controllerAddress: Address): Hex {
  const prefix = DATA_KEYS['AddressPermissions:AllowedCalls_prefix']
  const addressBytes = controllerAddress.slice(2).toLowerCase()
  return `${prefix}${addressBytes}` as Hex
}

/**
 * Get the data key for a controller's AllowedERC725YDataKeys
 */
export function getAllowedDataKeysDataKey(controllerAddress: Address): Hex {
  const prefix = DATA_KEYS['AddressPermissions:AllowedERC725YDataKeys_prefix']
  const addressBytes = controllerAddress.slice(2).toLowerCase()
  return `${prefix}${addressBytes}` as Hex
}

/**
 * Get the data key for a specific index in the AddressPermissions[] array
 */
export function getArrayIndexDataKey(index: number): Hex {
  const prefix = DATA_KEYS['AddressPermissions[]_index_prefix']
  const indexHex = pad(toHex(index), { size: 16 }).slice(2)
  return `${prefix}${indexHex}` as Hex
}

/**
 * Build the data keys and values for adding a new controller or updating an existing one
 * @param isExisting - If true, skip adding to the array (controller already exists)
 * @param emptySlotIndex - If provided and controller is new, use this empty slot instead of appending
 */
export function buildControllerData(
  controllerAddress: Address,
  permissions: Hex,
  currentLength: number,
  allowedCalls?: Hex,
  allowedDataKeys?: Hex,
  isExisting: boolean = false,
  emptySlotIndex: number | null = null
): { dataKeys: Hex[]; dataValues: Hex[] } {
  const dataKeys: Hex[] = []
  const dataValues: Hex[] = []

  // Only add to array if this is a new controller
  if (!isExisting) {
    if (emptySlotIndex !== null) {
      // Use the empty slot - no need to update array length
      // Just set the controller address at the empty slot index
      dataKeys.push(getArrayIndexDataKey(emptySlotIndex))
      dataValues.push(pad(controllerAddress, { size: 20 }))
    } else {
      // No empty slot found - append to end
      // 1. Update array length
      dataKeys.push(DATA_KEYS['AddressPermissions[]'] as Hex)
      dataValues.push(pad(toHex(currentLength + 1), { size: 16 }))

      // 2. Add controller address to array at new index
      dataKeys.push(getArrayIndexDataKey(currentLength))
      dataValues.push(pad(controllerAddress, { size: 20 }))
    }
  }

  // 3. Set permissions for controller (always do this)
  dataKeys.push(getPermissionsDataKey(controllerAddress))
  dataValues.push(permissions)

  // 4. Optionally set AllowedCalls
  if (allowedCalls) {
    dataKeys.push(getAllowedCallsDataKey(controllerAddress))
    dataValues.push(allowedCalls)
  }

  // 5. Optionally set AllowedERC725YDataKeys
  if (allowedDataKeys) {
    dataKeys.push(getAllowedDataKeysDataKey(controllerAddress))
    dataValues.push(allowedDataKeys)
  }

  return { dataKeys, dataValues }
}

/**
 * Encode AllowedCalls for a controller
 * @param calls Array of { callTypes, address, interfaceId, functionSelector }
 * LSP6 AllowedCalls tuple call-type bitmap: 0x1 = TRANSFERVALUE, 0x2 = CALL, 0x4 = STATICCALL, 0x8 = DELEGATECALL
 */
export interface AllowedCall {
  callTypes: number // See CALL_TYPES in constants (tuple bitmap, not permission bitmask)
  address: Address // Contract address (use 0xFFFF...FFFF for any)
  interfaceId: Hex // 4 bytes interface ID (use 0xFFFFFFFF for any)
  functionSelector: Hex // 4 bytes selector (use 0xFFFFFFFF for any)
}

const ANY_ADDRESS_LOWER = '0xffffffffffffffffffffffffffffffffffffffff'
const ANY_4BYTES_LOWER = '0xffffffff'

/**
 * Encode AllowedCalls per LIP-2 / LSP2:
 * valueType = (bytes4,address,bytes4,bytes4)[CompactBytesArray]
 * valueContent = (BitArray,Address,Bytes4,Bytes4)
 *
 * CompactBytesArray: each element is prefixed by 2 bytes (big-endian uint16) length, then data.
 * Each AllowedCalls element is fixed 32 bytes: callTypes(4) + address(20) + interfaceId(4) + functionSelector(4).
 * So each segment is 0x0020 (length 32) + 32 bytes.
 */
/**
 * True when the bitmask uses scoped call permissions (CALL, STATICCALL, …) without the matching
 * SUPER_* bits. LSP6 requires a non-empty AllowedCalls list in that case — updates that only
 * touch AddressPermissions:Permissions without AllowedCalls will revert.
 */
export function permissionsRequireNonEmptyAllowedCalls(permissions: bigint): boolean {
  const p = permissions
  const hasScopedCall =
    (p & BigInt(PERMISSIONS.CALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.STATICCALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.DELEGATECALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.TRANSFERVALUE)) !== 0n
  const hasSuperCall =
    (p & BigInt(PERMISSIONS.SUPER_CALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.SUPER_STATICCALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.SUPER_DELEGATECALL)) !== 0n ||
    (p & BigInt(PERMISSIONS.SUPER_TRANSFERVALUE)) !== 0n
  return hasScopedCall && !hasSuperCall
}

/** One LSP6 tuple: CALL to any address / any interface / any selector (fills the required list). */
export function encodeMinimalWildcardAllowedCalls(): Hex {
  return encodeAllowedCalls([
    {
      callTypes: CALL_TYPES.CALL,
      address: getAddress('0xFFfFfFffFFfffFFfFFfFFFFFffFFFffffFfFFFfF'),
      interfaceId: '0xffffffff' as Hex,
      functionSelector: '0xffffffff' as Hex,
    },
  ])
}

export function encodeAllowedCalls(calls: AllowedCall[]): Hex {
  if (calls.length === 0) return '0x' as Hex

  const encoded = calls.map(call => {
    const callTypes = pad(toHex(call.callTypes), { size: 4 }).slice(2)
    const address = call.address.toLowerCase().replace(/^0x/, '').padStart(40, '0')
    const interfaceId = call.interfaceId.replace(/^0x/, '').padStart(8, '0')
    const functionSelector = call.functionSelector.replace(/^0x/, '').padStart(8, '0')
    return `0020${callTypes}${address}${interfaceId}${functionSelector}`
  }).join('')

  return `0x${encoded}` as Hex
}

export function decodeAllowedCalls(encoded: Hex): AllowedCall[] {
  if (!encoded || encoded === '0x') return []
  const hex = encoded.slice(2)
  let i = 0
  const out: AllowedCall[] = []

  while (i + 4 <= hex.length) {
    const len = parseInt(hex.slice(i, i + 4), 16) // bytes
    i += 4
    const dataHex = hex.slice(i, i + len * 2)
    i += len * 2
    if (len !== 32 || dataHex.length !== 64) continue

    const callTypes = Number(BigInt(`0x${dataHex.slice(0, 8)}`))
    const address = `0x${dataHex.slice(8, 48)}` as Address
    const interfaceId = `0x${dataHex.slice(48, 56)}` as Hex
    const functionSelector = `0x${dataHex.slice(56, 64)}` as Hex
    out.push({ callTypes, address, interfaceId, functionSelector })
  }

  return out
}

/** Full on-chain tuple identity for deduplication (includes function selector). */
export function allowedCallTupleKey(c: AllowedCall): string {
  return `${c.callTypes}-${c.address.toLowerCase()}-${c.interfaceId.toLowerCase()}-${c.functionSelector.toLowerCase()}`
}

/** Union AllowedCalls tuples: existing first, then incoming; duplicates (same tuple key) skipped. */
export function mergeAllowedCallsDedupe(existing: AllowedCall[], incoming: AllowedCall[]): AllowedCall[] {
  const seen = new Set<string>()
  const out: AllowedCall[] = []
  for (const c of existing) {
    const k = allowedCallTupleKey(c)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(c)
  }
  for (const c of incoming) {
    const k = allowedCallTupleKey(c)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(c)
  }
  return out
}

/** Union AllowedERC725YDataKeys prefix list; case-insensitive hex dedupe; existing first. */
export function mergeAllowedDataKeysDedupe(existing: Hex[], incoming: Hex[]): Hex[] {
  const seen = new Set<string>()
  const out: Hex[] = []
  for (const k of existing) {
    const n = k.toLowerCase()
    if (seen.has(n)) continue
    seen.add(n)
    out.push(k)
  }
  for (const k of incoming) {
    const n = k.toLowerCase()
    if (seen.has(n)) continue
    seen.add(n)
    out.push(k)
  }
  return out
}

/**
 * Prefer on-chain bytes; if empty or yields no decoded tuples, fall back to LSP4
 * `template.allowedCallsHex` (mirrors publish form when chain payload is missing).
 */
export function resolveTemplateAllowedCallsHex(
  onChain: Hex,
  meta: { template?: { allowedCallsHex?: string } } | null,
): Hex {
  if (decodeAllowedCalls(onChain).length > 0) return onChain
  const raw = meta?.template?.allowedCallsHex?.trim()
  if (raw && /^0x[0-9a-fA-F]*$/.test(raw)) {
    const h = raw as Hex
    if (decodeAllowedCalls(h).length > 0) return h
  }
  return onChain
}

/**
 * Same as {@link resolveTemplateAllowedCallsHex} for AllowedERC725YDataKeys.
 */
export function resolveTemplateAllowedDataKeysHex(
  onChain: Hex,
  meta: { template?: { allowedERC725YDataKeysHex?: string } } | null,
): Hex {
  if (decodeAllowedDataKeys(onChain).length > 0) return onChain
  const raw = meta?.template?.allowedERC725YDataKeysHex?.trim()
  if (raw && /^0x[0-9a-fA-F]*$/.test(raw)) {
    const h = raw as Hex
    if (decodeAllowedDataKeys(h).length > 0) return h
  }
  return onChain
}

/**
 * Ordered labels for one AllowedCalls tuple bitmap (LSP6).
 * CALL / STATICCALL / DELEGATECALL before TRANSFERVALUE so contract-call intent reads first
 * when multiple bits are set (e.g. CALL+VALUE is shown as "CALL" first, not "VALUE" first).
 */
export function allowedCallsTupleBitmapLabels(bitmap: number): string[] {
  const parts: string[] = []
  if (bitmap & CALL_TYPES.CALL) parts.push('CALL')
  if (bitmap & CALL_TYPES.STATICCALL) parts.push('STATICCALL')
  if (bitmap & CALL_TYPES.DELEGATECALL) parts.push('DELEGATECALL')
  if (bitmap & CALL_TYPES.TRANSFERVALUE) parts.push('TRANSFERVALUE')
  return parts
}

/** Human-readable LSP6 call-type flags for AllowedCalls tuples. */
export function formatAllowedCallCallTypes(callTypes: number): string {
  const parts = allowedCallsTupleBitmapLabels(callTypes)
  if (parts.length === 0) return `bitmap ${callTypes}`
  return parts.join(' · ')
}

/**
 * Labels for one AllowedCalls tuple (for marketplace / review UI).
 * Uses the same "any" rules as {@link useControllers} decoding.
 */
export function describeAllowedCallTuple(call: AllowedCall): {
  callTypes: string
  address: string
  interfaceId: string
  functionSelector: string
} {
  const addrLower = call.address.toLowerCase()
  const isAnyAddr = addrLower === ANY_ADDRESS_LOWER
  const ifaceLower = call.interfaceId.toLowerCase()
  const isAnyIface = ifaceLower === ANY_4BYTES_LOWER
  const selLower = call.functionSelector.toLowerCase()
  const isAnySel = selLower === ANY_4BYTES_LOWER

  let ifaceLabel: string = call.interfaceId
  for (const v of Object.values(INTERFACE_PRESETS)) {
    if (v.id.toLowerCase() === ifaceLower) {
      ifaceLabel = `${v.name} (${v.id})`
      break
    }
  }
  if (isAnyIface) ifaceLabel = 'Any interface'

  return {
    callTypes: formatAllowedCallCallTypes(call.callTypes),
    address: isAnyAddr ? 'Any contract' : formatAddress(call.address, 6),
    interfaceId: ifaceLabel,
    functionSelector: isAnySel ? 'Any function' : call.functionSelector,
  }
}

/** Best-effort label for an allowed data key prefix (matches known LSP / preset keys). */
export function describeDataKeyPrefix(key: Hex): string | null {
  const kn = key.toLowerCase()
  if (kn.length < 4) return null
  let best: { len: number; name: string } | null = null
  for (const preset of Object.values(DATA_KEY_PRESETS)) {
    const pk = preset.key.toLowerCase()
    if (pk.startsWith(kn)) {
      if (!best || pk.length > best.len) best = { len: pk.length, name: preset.name }
    }
  }
  for (const [label, v] of Object.entries(DATA_KEYS)) {
    if (typeof v !== 'string') continue
    const pk = v.toLowerCase()
    if (pk.startsWith(kn)) {
      if (!best || pk.length > best.len) best = { len: pk.length, name: label }
    }
  }
  return best?.name ?? null
}

const NAME_SYMBOL_ABI = [
  { inputs: [], name: 'name', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'symbol', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
] as const

const LSP4_TOKEN_NAME_KEY = '0xdeba1e292f8ba88238e10ab3c7f88bd4be4fac56cad5194b6ecceaf653468af1' as Hex
const LSP4_TOKEN_SYMBOL_KEY = '0x2f0a68ab07768e01943a599e73362a0e17a63a72e94dd2e384d2c1d4db932756' as Hex
const ERC725Y_GET_DATA_ABI = [
  { inputs: [{ name: 'dataKey', type: 'bytes32' }], name: 'getData', outputs: [{ type: 'bytes', name: 'value' }], stateMutability: 'view', type: 'function' },
] as const

function decodeLSP2StringValue(hex: Hex): string | undefined {
  if (!hex || hex === '0x' || hex.length < 4) return undefined
  const raw = hex.slice(2)
  try {
    if (raw.length >= 4) {
      const len = parseInt(raw.slice(0, 4), 16)
      if (len > 0 && raw.length >= 4 + len * 2) {
        return hexToString(('0x' + raw.slice(4, 4 + len * 2)) as Hex).replace(/\u0000+$/g, '').trim() || undefined
      }
    }
    return hexToString(hex as Hex).replace(/\u0000+$/g, '').trim() || undefined
  } catch {
    return undefined
  }
}

/**
 * Read ERC20/LSP7-style name + symbol from a contract (same strategy as ControllersView).
 */
export async function fetchContractNameSymbol(
  publicClient: PublicClient,
  address: Address,
): Promise<{ name?: string; symbol?: string } | null> {
  try {
    const code = await publicClient.getCode({ address })
    if (!code || code === '0x') return null
    let name = await publicClient.readContract({ address, abi: NAME_SYMBOL_ABI, functionName: 'name' }).catch(() => undefined) as string | undefined
    let symbol = await publicClient.readContract({ address, abi: NAME_SYMBOL_ABI, functionName: 'symbol' }).catch(() => undefined) as string | undefined
    if (name === undefined || symbol === undefined) {
      const [nameBytes, symbolBytes] = await Promise.all([
        publicClient.readContract({ address, abi: ERC725Y_GET_DATA_ABI, functionName: 'getData', args: [LSP4_TOKEN_NAME_KEY] }).catch(() => '0x' as Hex),
        publicClient.readContract({ address, abi: ERC725Y_GET_DATA_ABI, functionName: 'getData', args: [LSP4_TOKEN_SYMBOL_KEY] }).catch(() => '0x' as Hex),
      ])
      if (name === undefined && nameBytes && nameBytes !== '0x') name = decodeLSP2StringValue(nameBytes as Hex)
      if (symbol === undefined && symbolBytes && symbolBytes !== '0x') symbol = decodeLSP2StringValue(symbolBytes as Hex)
    }
    if (name === undefined && symbol === undefined) return null
    return { name, symbol }
  } catch {
    return null
  }
}

/** Resolve a 4-byte selector to a text signature via 4byte.directory (best effort). */
export async function fetchFunctionSignature4byte(selector: Hex): Promise<string | null> {
  const hex = selector.toLowerCase()
  if (hex === ANY_4BYTES_LOWER) return null
  try {
    const res = await fetch(
      `https://www.4byte.directory/api/v1/signatures/?hex_signature=${encodeURIComponent(hex)}`
    )
    if (!res.ok) return null
    const json = (await res.json()) as { results?: Array<{ text_signature?: string }> }
    return json.results?.[0]?.text_signature ?? null
  } catch {
    return null
  }
}

/**
 * Human-readable target line(s) for an AllowedCalls tuple (profile name, contract name, or address).
 */
export async function resolveAllowedCallTargetLabel(
  publicClient: PublicClient | null,
  call: AllowedCall,
): Promise<{ primary: string; secondary?: string }> {
  const addrLower = call.address.toLowerCase()
  if (addrLower === ANY_ADDRESS_LOWER) return { primary: 'Any contract' }

  const profile = await fetchProfileFromIndexer(call.address)
  if (profile?.name?.trim()) {
    return { primary: profile.name.trim(), secondary: formatAddress(call.address, 6) }
  }

  if (!publicClient) {
    return { primary: formatAddress(call.address, 6) }
  }

  const meta = await fetchContractNameSymbol(publicClient, call.address)
  if (meta?.name?.trim()) {
    const primary = meta.symbol?.trim() ? `${meta.name.trim()} (${meta.symbol.trim()})` : meta.name.trim()
    return { primary, secondary: formatAddress(call.address, 6) }
  }

  return { primary: formatAddress(call.address, 6) }
}

/**
 * Human-readable function line(s): 4byte text signature when available, else raw selector.
 */
export async function resolveAllowedCallFunctionLabel(selector: Hex): Promise<{ primary: string; secondary?: string }> {
  const selLower = selector.toLowerCase()
  if (selLower === ANY_4BYTES_LOWER) return { primary: 'Any function' }

  const sig = await fetchFunctionSignature4byte(selector)
  if (sig) return { primary: sig, secondary: selector }
  return { primary: selector }
}

/** Short label for an allowed data key prefix (hex). */
export function formatAllowedDataKeyPreview(key: Hex, headChars = 10): string {
  const k = key.toLowerCase()
  if (k.length <= 2 + headChars * 2) return key
  return `${key.slice(0, 2 + headChars)}…${key.slice(-6)}`
}

/**
 * Encode AllowedERC725YDataKeys
 * @param dataKeyPrefixes Array of data key prefixes to allow
 */
export function encodeAllowedDataKeys(dataKeyPrefixes: Hex[]): Hex {
  if (dataKeyPrefixes.length === 0) return '0x' as Hex

  // CompactBytesArray format: length (2 bytes) + data
  const parts = dataKeyPrefixes.map(prefix => {
    const length = (prefix.length - 2) / 2 // bytes length
    const lengthHex = pad(toHex(length), { size: 2 }).slice(2)
    return lengthHex + prefix.slice(2)
  })

  return `0x${parts.join('')}` as Hex
}

export function decodeAllowedDataKeys(encoded: Hex): Hex[] {
  if (!encoded || encoded === '0x') return []
  const hex = encoded.slice(2)
  let i = 0
  const out: Hex[] = []

  while (i + 4 <= hex.length) {
    const len = parseInt(hex.slice(i, i + 4), 16) // bytes
    i += 4
    const dataHex = hex.slice(i, i + len * 2)
    i += len * 2
    out.push(`0x${dataHex}` as Hex)
  }

  return out
}

/**
 * AllowedCallEntry — UI state model for a single AllowedCalls entry
 */
export interface AllowedCallEntry {
  id: string
  callTypes: { call: boolean; staticCall: boolean; delegateCall: boolean; transferValue: boolean }
  address: string
  useAnyAddress: boolean
  interfaceId: string
  useAnyInterface: boolean
  /** @deprecated Prefer `functionSignatures`; still used when that array is empty */
  functionInput: string
  /**
   * Multiple functions on the same target. Each becomes one LSP6 AllowedCalls tuple on-chain
   * (same address / interface / call types, different 4-byte selector).
   */
  functionSignatures: string[]
  useAnyFunction: boolean
}

function allowedCallGroupKey(c: AllowedCall): string {
  return `${c.callTypes}-${c.address.toLowerCase()}-${c.interfaceId.toLowerCase()}`
}

/** Normalize for comparing ABI picks vs stored hex or signature strings. */
export function allowedCallSignatureKey(sig: string): string {
  const t = sig.trim()
  if (/^0x[a-fA-F0-9]{8}$/.test(t)) return t.toLowerCase()
  const sel = computeSelector(t)
  return sel ? sel.toLowerCase() : ''
}

/** Effective list: multi-select array, else legacy single `functionInput`. */
export function getEffectiveFunctionSignatures(entry: AllowedCallEntry): string[] {
  if (Array.isArray(entry.functionSignatures) && entry.functionSignatures.length > 0) return entry.functionSignatures
  if (entry.functionInput.trim()) return [entry.functionInput.trim()]
  return []
}

export function convertAllowedCallsToEntries(calls: AllowedCall[]): AllowedCallEntry[] {
  const buckets = new Map<string, AllowedCall[]>()
  for (const c of calls) {
    const k = allowedCallGroupKey(c)
    if (!buckets.has(k)) buckets.set(k, [])
    buckets.get(k)!.push(c)
  }

  return Array.from(buckets.values()).map((group) => {
    const c = group[0]
    const addressLower = c.address.toLowerCase()
    const ifaceLower = c.interfaceId.toLowerCase()
    const useAnyAddress = addressLower === ANY_ADDRESS_LOWER
    const useAnyInterface = ifaceLower === ANY_4BYTES_LOWER
    const useAnyFunction = group.every((x) => x.functionSelector.toLowerCase() === ANY_4BYTES_LOWER)

    const functionSignatures = useAnyFunction
      ? []
      : group.map((x) => x.functionSelector.toLowerCase() as string)

    return {
      id: crypto.randomUUID(),
      callTypes: {
        call: (c.callTypes & CALL_TYPES.CALL) !== 0,
        staticCall: (c.callTypes & CALL_TYPES.STATICCALL) !== 0,
        delegateCall: (c.callTypes & CALL_TYPES.DELEGATECALL) !== 0,
        transferValue: (c.callTypes & CALL_TYPES.TRANSFERVALUE) !== 0,
      },
      address: useAnyAddress ? '' : c.address,
      useAnyAddress,
      interfaceId: useAnyInterface ? '' : c.interfaceId,
      useAnyInterface,
      functionInput: functionSignatures.length === 1 ? functionSignatures[0] : '',
      functionSignatures,
      useAnyFunction,
    }
  })
}

/**
 * DataKeyEntry — UI state model for a single AllowedERC725YDataKeys entry
 */
export interface DataKeyEntry {
  id: string
  name: string
  key: string
  isPreset: boolean
}

export function convertAllowedDataKeysToEntries(keys: Hex[]): DataKeyEntry[] {
  const presets = Object.values(DATA_KEY_PRESETS)
  return keys.map((k) => {
    const preset = presets.find((p) => p.key.toLowerCase() === k.toLowerCase())
    return {
      id: crypto.randomUUID(),
      name: preset ? preset.name : 'Custom Key',
      key: k,
      isPreset: !!preset,
    }
  })
}

/**
 * Compute a function selector from hex or human-readable signature
 */
export function computeSelector(input: string): Hex | null {
  if (!input || input.trim() === '') return null
  const trimmed = input.trim()
  // Already a 4-byte hex selector
  if (/^0x[a-fA-F0-9]{8}$/.test(trimmed)) {
    return trimmed.toLowerCase() as Hex
  }
  // Try to parse as function signature
  try {
    return toFunctionSelector(trimmed)
  } catch {
    return null
  }
}

/**
 * Convert UI AllowedCallEntry[] to AllowedCall[] for encoding.
 * Multiple `functionSignatures` on one entry expand to multiple on-chain tuples (LSP6: one selector per element).
 */
export function convertEntriesToAllowedCalls(entries: AllowedCallEntry[]): AllowedCall[] {
  const out: AllowedCall[] = []

  for (const entry of entries) {
    let callTypeBitmap = 0
    if (entry.callTypes.transferValue) callTypeBitmap |= CALL_TYPES.TRANSFERVALUE
    if (entry.callTypes.call) callTypeBitmap |= CALL_TYPES.CALL
    if (entry.callTypes.staticCall) callTypeBitmap |= CALL_TYPES.STATICCALL
    if (entry.callTypes.delegateCall) callTypeBitmap |= CALL_TYPES.DELEGATECALL

    const address = entry.useAnyAddress
      ? '0xFFfFfFffFFfffFFfFFfFFFFFffFFFffffFfFFFfF' as Address
      : entry.address as Address

    const interfaceId = entry.useAnyInterface
      ? '0xFFFFFFFF' as Hex
      : (entry.interfaceId || '0xFFFFFFFF') as Hex

    if (entry.useAnyFunction) {
      out.push({ callTypes: callTypeBitmap, address, interfaceId, functionSelector: '0xFFFFFFFF' as Hex })
      continue
    }

    const sigs = getEffectiveFunctionSignatures(entry)
    if (sigs.length === 0) {
      out.push({ callTypes: callTypeBitmap, address, interfaceId, functionSelector: '0xFFFFFFFF' as Hex })
      continue
    }

    let anyPushed = false
    for (const sig of sigs) {
      const sel = computeSelector(sig)
      if (!sel) continue
      out.push({ callTypes: callTypeBitmap, address, interfaceId, functionSelector: sel })
      anyPushed = true
    }
    if (!anyPushed) {
      out.push({ callTypes: callTypeBitmap, address, interfaceId, functionSelector: '0xFFFFFFFF' as Hex })
    }
  }

  return out
}

/**
 * Ensure AllowedCalls restrictions use scoped CALL instead of SUPER_CALL.
 */
export function applyAllowedCallsScopedInvariant(permissions: bigint): bigint {
  let next = permissions
  next = next & ~BigInt(PERMISSIONS.SUPER_CALL)
  next = next | BigInt(PERMISSIONS.CALL)
  return next
}

/**
 * Ensure AllowedERC725YDataKeys restrictions use scoped SETDATA instead of SUPER_SETDATA.
 */
export function applyAllowedDataKeysScopedInvariant(permissions: bigint): bigint {
  let next = permissions
  next = next & ~BigInt(PERMISSIONS.SUPER_SETDATA)
  next = next | BigInt(PERMISSIONS.SETDATA)
  return next
}

/**
 * Validate permissions and return warnings/risks
 */
export interface PermissionValidation {
  valid: boolean
  warnings: string[]
  risks: string[]
}

export function validatePermissions(permissions: string | bigint): PermissionValidation {
  const warnings: string[] = []
  const risks: string[] = []
  const permValue = BigInt(permissions)

  for (const [name, hex] of Object.entries(PERMISSIONS)) {
    if (name === 'ALL_PERMISSIONS') continue
    if ((permValue & BigInt(hex)) !== 0n) {
      const risk = PERMISSION_RISK[name]
      if (risk === 'critical') {
        risks.push(`${name}: This is an extremely dangerous permission`)
      } else if (risk === 'high') {
        warnings.push(`${name}: This permission requires caution`)
      }
    }
  }

  return {
    valid: risks.length === 0,
    warnings,
    risks,
  }
}

/**
 * Format an address for display (truncated)
 */
export function formatAddress(address: string, chars: number = 4): string {
  if (!address || address.length < 10) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

/**
 * Parse URL parameters for pre-filled values
 */
export function parseUrlParams(): {
  controllerAddress?: Address
  upAddress?: Address
  chainId?: number
  preset?: string
} {
  const params = new URLSearchParams(window.location.search)
  return {
    controllerAddress: params.get('controller') as Address | undefined,
    upAddress: params.get('up') as Address | undefined,
    chainId: params.get('chain') ? parseInt(params.get('chain')!) : undefined,
    preset: params.get('preset') || undefined,
  }
}

/**
 * Generate authorization URL with parameters
 */
export function generateAuthUrl(
  baseUrl: string,
  controllerAddress: Address,
  preset?: string,
  chainId?: number
): string {
  const params = new URLSearchParams()
  params.set('controller', controllerAddress)
  if (preset) params.set('preset', preset)
  if (chainId) params.set('chain', chainId.toString())
  return `${baseUrl}?${params.toString()}`
}

/**
 * Convert IPFS URL to HTTP gateway URL
 * Handles ipfs://, ipfs://ipfs/, and raw CIDs
 * Uses LUKSO's gateway as primary, with cloudflare-ipfs as a reliable alternative
 */
export const IPFS_GATEWAY = 'https://api.universalprofile.cloud/ipfs/'

export function convertIpfsUrl(url: string): string {
  if (!url) return url
  
  // Already an HTTP URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  
  // IPFS protocol URLs
  if (url.startsWith('ipfs://')) {
    const hash = url.replace('ipfs://', '').replace(/^ipfs\//, '')
    return `${IPFS_GATEWAY}${hash}`
  }
  
  // Raw IPFS hash (starts with Qm or baf)
  if (url.startsWith('Qm') || url.startsWith('baf')) {
    return `${IPFS_GATEWAY}${url}`
  }
  
  return url
}

/**
 * Marketplace / UI shape for template metadata (legacy flat JSON or normalized from LSP4).
 */
export type TemplateMetadata = {
  name?: string
  description?: string
  app?: {
    name?: string
    website?: string
    docs?: string
    publisherUP?: string
    /** Controller address that should receive these permissions when the template is installed */
    controllerAddress?: string
  }
  branding?: { icon?: string; banner?: string; primaryColor?: string }
  template?: {
    slug?: string
    version?: number
    chainId?: number
    /** Mirrors LSP4 attributes when publishing (optional; on-chain bytes are canonical). */
    permissionsHex?: string
    allowedCallsHex?: string
    allowedERC725YDataKeysHex?: string
  }
}

/** LSP4 Digital Asset Metadata document root (matches LUKSO/LSP4 JSON shape). */
export type Lsp4TemplateMetadataDocument = {
  LSP4Metadata: {
    name: string
    description?: string
    links: Array<{ title: string; url: string }>
    icon: unknown[]
    images: unknown[]
    attributes: Array<{ key: string; value: string; type: string }>
  }
}

export type BuildLsp4TemplateMetadataInput = {
  displayName: string
  description?: string
  slug: string
  version: number
  chainId: number
  /** Name of the app creating this template (stored as LSP4 attribute `app`). */
  app: string
  /** Address of the controller that will receive these permissions when someone installs the template. */
  controllerAddress: string
  publisherUP?: string
  website?: string
  docsUrl?: string
  primaryColor?: string
  /** bytes32 hex — mirrors `publishTemplate` `permissions`. */
  permissionsHex?: string
  /** CompactBytesArray hex — mirrors on-chain `allowedCalls`. */
  allowedCallsHex?: string
  /** CompactBytesArray hex — mirrors on-chain `allowedERC725YDataKeys`. */
  allowedERC725YDataKeysHex?: string
}

/**
 * Build LSP4-shaped metadata JSON (same pattern as Groov / LUKSO mints: `LSP4Metadata` + links + attributes).
 * `publishTemplate` stores `metadataURI` as a string — typically `ipfs://…` after pinning this document.
 */
export function buildLsp4TemplateMetadataDocument(input: BuildLsp4TemplateMetadataInput): Lsp4TemplateMetadataDocument {
  const links: Array<{ title: string; url: string }> = []
  if (input.website?.trim()) links.push({ title: 'Website', url: input.website.trim() })
  if (input.docsUrl?.trim()) links.push({ title: 'Documentation', url: input.docsUrl.trim() })

  const attributes: Array<{ key: string; value: string; type: string }> = [
    { key: 'templateSlug', value: input.slug, type: 'string' },
    { key: 'templateVersion', value: String(input.version), type: 'string' },
    { key: 'chainId', value: String(input.chainId), type: 'string' },
    { key: 'app', value: input.app.trim(), type: 'string' },
    { key: 'controllerAddress', value: input.controllerAddress.trim(), type: 'string' },
  ]
  if (input.publisherUP) attributes.push({ key: 'publisherUP', value: input.publisherUP, type: 'string' })
  if (input.primaryColor) attributes.push({ key: 'primaryColor', value: input.primaryColor, type: 'string' })
  if (input.permissionsHex?.trim())
    attributes.push({ key: 'permissions', value: input.permissionsHex.trim(), type: 'string' })
  if (input.allowedCallsHex?.trim())
    attributes.push({ key: 'allowedCalls', value: input.allowedCallsHex.trim(), type: 'string' })
  if (input.allowedERC725YDataKeysHex?.trim())
    attributes.push({
      key: 'allowedERC725YDataKeys',
      value: input.allowedERC725YDataKeysHex.trim(),
      type: 'string',
    })

  return {
    LSP4Metadata: {
      name: input.displayName,
      ...(input.description?.trim() ? { description: input.description.trim() } : {}),
      links,
      icon: [],
      images: [],
      attributes,
    },
  }
}

/**
 * LSP2 **VerifiableURI** value for the `LSP4Metadata` data key (non-verifiable URI: no hash proof).
 * Explorers and `@erc725/erc725.js` decode this shape; they do **not** treat raw `bytes(string)` as valid LSP4.
 *
 * Layout (LSP-2): `bytes2 identifier 0x0000` + `bytes4 verificationMethod 0x00000000` + `bytes2 dataLen 0x0000` + UTF-8 URI bytes.
 *
 * **Contract fix:** `PermissionControl` should use this encoding in `_setDataForTokenId(tokenId, LSP4Metadata, …)`
 * instead of `bytes(metadataURI)`. The `metadataURI` string in `TemplateData` / `publishTemplate` can stay as-is for your app;
 * only the ERC725Y **value bytes** must be VerifiableURI-shaped for wallets and Universal Everything to render the NFT.
 */
export function encodeLsp4MetadataVerifiableUriValue(uri: string): Hex {
  return concatHex([
    '0x0000',
    '0x00000000',
    '0x0000',
    stringToHex(uri),
  ])
}

/**
 * Pin JSON to IPFS via Pinata (browser `pinJSONToIPFS`). Requires `VITE_PINATA_JWT`.
 *
 * **Note:** For ERC725Y `LSP4Metadata` storage, the on-chain **value** must be {@link encodeLsp4MetadataVerifiableUriValue}
 * (or full `@erc725/erc725.js` encodeData with JSON+URL). `PermissionControl.publishTemplate` accepts a **`string metadataURI`**
 * for the template struct and events; that same URI should be passed through **VerifiableURI encoding** when writing `setDataForTokenId`.
 */
export async function pinJsonToIpfs(content: unknown, jwt: string): Promise<string> {
  const res = await fetch('https://api.pinata.cloud/pinning/pinJSONToIPFS', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      pinataContent: content,
      pinataMetadata: { name: 'permissions-onchain-template.json' },
    }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Pinata pin failed: ${res.status} ${text.slice(0, 200)}`)
  }
  const data = (await res.json()) as { IpfsHash?: string; cid?: string }
  const hash = data.IpfsHash ?? data.cid
  if (!hash) throw new Error('Pinata response missing IpfsHash')
  return `ipfs://${hash}`
}

/**
 * Normalize fetched JSON (LSP4 wrapper or legacy flat `{ name, app, template }`) into `TemplateMetadata`.
 */
export function normalizeTemplateMetadataFromJson(raw: unknown): TemplateMetadata | null {
  if (raw === null || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const lsp4 = obj.LSP4Metadata
  if (lsp4 && typeof lsp4 === 'object') {
    const m = lsp4 as Record<string, unknown>
    const name = typeof m.name === 'string' ? m.name : undefined
    const description = typeof m.description === 'string' ? m.description : undefined
    const links = Array.isArray(m.links) ? m.links : []
    const attrByKey = new Map<string, string>()
    if (Array.isArray(m.attributes)) {
      for (const a of m.attributes) {
        if (a && typeof a === 'object' && 'key' in a && 'value' in a) {
          attrByKey.set(String((a as { key: unknown }).key), String((a as { value: unknown }).value))
        }
      }
    }
    const websiteLink = links.find(
      (l: unknown) =>
        l &&
        typeof l === 'object' &&
        (l as { title?: string }).title === 'Website' &&
        typeof (l as { url?: string }).url === 'string',
    ) as { url?: string } | undefined
    const docsLink = links.find(
      (l: unknown) =>
        l &&
        typeof l === 'object' &&
        (l as { title?: string }).title === 'Documentation' &&
        typeof (l as { url?: string }).url === 'string',
    ) as { url?: string } | undefined

    const templateSlug = attrByKey.get('templateSlug')
    const templateVersion = attrByKey.get('templateVersion')
    const chainIdAttr = attrByKey.get('chainId')
    const appName =
      attrByKey.get('app') ?? attrByKey.get('appName')
    const controllerAddress = attrByKey.get('controllerAddress')
    const publisherUP = attrByKey.get('publisherUP')
    const primaryColor = attrByKey.get('primaryColor')
    const permissionsHex = attrByKey.get('permissions')
    const allowedCallsHexAttr = attrByKey.get('allowedCalls')
    const allowedERC725YDataKeysHexAttr = attrByKey.get('allowedERC725YDataKeys')

    return {
      name,
      description,
      app: {
        name: appName,
        website: websiteLink?.url?.trim() || undefined,
        docs: docsLink?.url?.trim() || undefined,
        publisherUP: publisherUP || undefined,
        controllerAddress: controllerAddress || undefined,
      },
      branding: primaryColor ? { primaryColor } : undefined,
      template: {
        slug: templateSlug,
        version: templateVersion ? parseInt(templateVersion, 10) : undefined,
        chainId: chainIdAttr ? parseInt(chainIdAttr, 10) : undefined,
        permissionsHex: permissionsHex || undefined,
        allowedCallsHex: allowedCallsHexAttr || undefined,
        allowedERC725YDataKeysHex: allowedERC725YDataKeysHexAttr || undefined,
      },
    }
  }

  if ('name' in obj || 'description' in obj || 'app' in obj || 'template' in obj || 'branding' in obj) {
    return {
      name: typeof obj.name === 'string' ? obj.name : undefined,
      description: typeof obj.description === 'string' ? obj.description : undefined,
      app: obj.app && typeof obj.app === 'object' ? (obj.app as TemplateMetadata['app']) : undefined,
      branding: obj.branding && typeof obj.branding === 'object' ? (obj.branding as TemplateMetadata['branding']) : undefined,
      template: obj.template && typeof obj.template === 'object' ? (obj.template as TemplateMetadata['template']) : undefined,
    }
  }

  return null
}

/**
 * Parse template metadata when `metadataURI` is inline JSON (e.g. mint stores JSON string on PermissionControl)
 * or a `data:application/json` URI. Returns null if not parseable.
 */
export function parseTemplateMetadataFromUri(metadataURI: string): unknown | null {
  const t = metadataURI.trim()
  if (t.startsWith('{')) {
    try {
      return JSON.parse(t)
    } catch {
      return null
    }
  }
  if (t.startsWith('data:application/json')) {
    const comma = t.indexOf(',')
    if (comma === -1) return null
    const payload = t.slice(comma + 1)
    try {
      if (/;base64/i.test(t)) {
        const b64 = payload
        const binary = atob(b64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        const text = new TextDecoder().decode(bytes)
        return JSON.parse(text)
      }
      return JSON.parse(decodeURIComponent(payload))
    } catch {
      return null
    }
  }
  return null
}

/**
 * Fetch profile data from LUKSO's Envio indexer.
 * Returns pre-resolved HTTP URLs for profile images.
 */
const LUKSO_INDEXER = 'https://envio.lukso-mainnet.universal.tech/v1/graphql'

export async function fetchProfileFromIndexer(address: string): Promise<{
  name: string | null
  profileImageUrl: string | null
} | null> {
  const query = `
    query GetProfile($address: String!) {
      Profile(where: { id: { _eq: $address } }) {
        name
        profileImages(
          where: { error: { _is_null: true } }
          order_by: { width: asc }
        ) {
          width
          src
          url
        }
      }
    }
  `

  const response = await fetch(LUKSO_INDEXER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query,
      variables: { address: address.toLowerCase() },
    }),
  })

  if (!response.ok) return null

  const json = await response.json()
  const profile = json?.data?.Profile?.[0]
  if (!profile) return null

  // Get best image — prefer ~200px width for medium size
  let bestImage: string | null = null
  const images = profile.profileImages || []
  if (images.length > 0) {
    // Sort by width, find closest to 200px
    const sorted = [...images].sort((a: { width: number }, b: { width: number }) => a.width - b.width)
    let best = sorted[0]
    let bestDiff = Math.abs(best.width - 200)
    for (const img of sorted) {
      const diff = Math.abs(img.width - 200)
      if (diff < bestDiff) {
        best = img
        bestDiff = diff
      }
    }
    // Prefer src (HTTP) over url (IPFS)
    const rawUrl = best.src || best.url
    if (rawUrl) {
      bestImage = rawUrl.startsWith('ipfs://')
        ? `https://api.universalprofile.cloud/image/${rawUrl.replace('ipfs://', '')}`
        : rawUrl
    }
  }

  return {
    name: profile.name || null,
    profileImageUrl: bestImage,
  }
}

/**
 * Find a preset that exactly matches the given permissions
 * Returns the preset key if found, null otherwise
 */
export function findMatchingPreset(permissions: bigint): string | null {
  for (const [key, preset] of Object.entries(PERMISSION_PRESETS)) {
    if (preset.permissions === permissions) {
      return key
    }
  }
  return null
}

/**
 * Parse permissions from a hex string (as returned from chain)
 * Returns the bigint value
 */
export function parsePermissionsHex(hex: Hex | string): bigint {
  if (!hex || hex === '0x') return 0n
  return BigInt(hex)
}

/**
 * Build a full 32-byte LSP2 Mapping key from a 12-byte prefix and an address.
 * Full key = first10bytes(keyName hash) + 0000 + last20bytes(keccak256(address))
 * The prefix already contains the first 12 bytes (10 bytes of key name hash + 2 zero bytes).
 */
export function buildMappingKey(prefix: string, address: string): Hex {
  // prefix is 12 bytes = "0x" + 24 hex chars
  const prefixClean = prefix.toLowerCase()
  // Hash the address and take last 20 bytes (40 hex chars)
  const addressHash = keccak256(address.toLowerCase() as Hex)
  const last20Bytes = addressHash.slice(-40)
  return `${prefixClean}${last20Bytes}` as Hex
}

/**
 * Deterministic bytes32 key for template metadata stored on a publisher UP.
 * This is used for `metadataURI = "erc725y:<dataKey>"`.
 */
export function getTemplateMetadataDataKey(publisherUP: Address, templateId: Hex, version: number): Hex {
  const ns = toHex('PermissionsOnchain:TemplateMetadata:')
  const addr20 = pad(publisherUP, { size: 20 })
  const v4 = pad(toHex(BigInt(version)), { size: 4 })
  return keccak256(concatHex([ns, addr20, templateId, v4])) as Hex
}

/**
 * LSP2 mapping prefix for a key name:
 * first10bytes(keccak256(keyName)) + 0000
 */
export function getLsp2MappingPrefix(keyName: string): Hex {
  const keyHash = keccak256(toHex(keyName))
  const first10Bytes = keyHash.slice(2, 22) // 20 hex chars = 10 bytes
  return `0x${first10Bytes}0000` as Hex
}

/**
 * Data key for a human-readable controller label on a UP.
 * keyName: PermissionsOnchain:ControllerLabel
 */
export function getControllerLabelDataKey(controllerAddress: Address): Hex {
  const prefix = getLsp2MappingPrefix('PermissionsOnchain:ControllerLabel')
  return buildMappingKey(prefix, controllerAddress)
}
